/**
 * Header Component - Expert Refactor
 * 
 * Improvements:
 * - Strategic memo usage (only root component)
 * - Custom hooks for cleaner logic separation
 * - Reusable utilities and constants
 * - Better scroll performance with throttling
 * - Enhanced accessibility and keyboard navigation
 * - Simplified logo logic and color mapping
 * - Proper cleanup and memory management
 */

import React, { memo, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Assets
import logo_yellow from "@assets/images/logo_yellow.svg";
import logo_green from "@assets/images/logo_green.svg";
import logo_purple from "@assets/images/logo_purple.svg";
import logo_black_white from "@assets/images/logo_black_white.svg";
import logo_violet from "@assets/images/logo_violet.svg";
import wallet from "@assets/images/wallet.svg";
import mobile_wallet from "@assets/images/mobile_wallet.svg";
import WalletIcon from "@assets/images/WalletIcon.jsx";
import ham from "@assets/images/ham_burger.svg";
import HamburgerIcon from "@assets/images/HamburgerIcon.jsx";

// Context and Hooks
import { useAppContext } from "@context/AppContext";
import { useScrollPosition } from "@hooks/useScrollPosition";
import { useWalletDisplay } from "@hooks/useWalletDisplay";
import { useMobileMenu } from "@hooks/useMobileMenu";
import { useHashNavigation } from "@hooks/useHashNavigation";

// Utils
import { logInfo } from "@utils/log";
import { isAndroid, isIOS } from "react-device-detect";

// Constants
const SCROLL_THRESHOLD = 66;
const ACTIVE_ROUTES = ['mint', 'buy', 'faqs'];

const LOGO_MAP = {
  yellow: logo_yellow,
  purple: logo_purple,
  green: logo_green,
  violet: logo_violet,
  black_white: logo_black_white,
};

const COLOR_MAP = {
  yellow: "#FCDB04",
  purple: "#B4519E",
  violet: "#8166AD",
  green: "#70C6A4",
  black_white: "#fff",
};

// Navigation menu items configuration
const MENU_ITEMS = [
  { 
    id: 'home', 
    label: 'Home', 
    to: '/#home', 
    isHash: true, 
    offset: 0 
  },
  { 
    id: 'top', 
    label: 'Top', 
    to: '/#top', 
    isHash: true, 
    offset: 0 
  },
  { 
    id: 'my', 
    label: 'My', 
    to: '/#my', 
    isHash: true, 
    offset: 0 
  },
  { 
    id: 'about', 
    label: 'About', 
    to: '/#about', 
    isHash: true, 
    offset: 0 
  },
  { 
    id: 'mint', 
    label: 'Donate',
    to: '/mint', 
    isHash: false 
  },
  {
    id: 'buy',
    label: 'Buy',
    to: '/buy',
    isHash: false,
    hidden: true
  },
];

// Logo Component
const Logo = ({ themeConfig }) => {
  const logoKey = themeConfig?.APP_MAIN_LOGO || 'yellow';
  const logoSrc = LOGO_MAP[logoKey] || logo_yellow;
  
  return (
    <a
      href={themeConfig?.APP_FUNDRAISIN_WEBSITE_LINK || '#'}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Visit Fund Raisin website"
    >
      <img 
        className="nav__logo" 
        src={logoSrc} 
        alt="Fund Raisin logo" 
        loading="lazy"
      />
    </a>
  );
};

// Menu Item Component
const MenuItem = ({ item, iconColor, onClick, isMobile = false }) => {
  const navigate = useNavigate();
  const navigateToHash = useHashNavigation();
  
  const handleClick = useCallback((e) => {
    if (item.isHash) {
      e.preventDefault();
      const target = item.to.split('#')[1];
      navigateToHash(target, item.offset);
    } else {
      navigate(item.to);
    }
    
    if (onClick) onClick();
  }, [item, navigate, navigateToHash, onClick]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  }, [handleClick]);

  return (
    <div
      className="nav__menu-item-box"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Navigate to ${item.label}`}
    >
      <li className="nav__menu-item">{item.label}</li>
      <div 
        className="nav__menu-item-icon" 
        style={{ backgroundColor: iconColor }}
        aria-hidden="true"
      />
    </div>
  );
};

// Wallet Button Component
const WalletButton = ({ walletAddress, displayAddress, onConnect, isMobile = false }) => (
  <span className="nav__menu-item-box">
    <li
      className="nav__menu-item nav__menu-item-btn"
      onClick={onConnect}
      role="button"
      tabIndex={0}
      aria-label={walletAddress ? `Wallet connected: ${displayAddress}` : "Connect your wallet"}
    >
      <div className="nav__menu-item-btn-info">
        <span className="wallet-addr">
          {walletAddress && displayAddress ? displayAddress : "Connect Your Wallet"}
        </span>
        <WalletIcon className="wallet-icon" walletAddress={walletAddress} displayAddress={displayAddress}/>
      </div>
    </li>
  </span>
);

// Desktop Navigation Component
const DesktopNav = ({ menuItems, iconColor, walletProps }) => (
  <div className="nav__menu-box">
    <ul className="nav__menu-items">
      {menuItems.map((item) => (
        <MenuItem 
          key={item.id} 
          item={item} 
          iconColor={iconColor} 
        />
      ))}
      <WalletButton {...walletProps} />
    </ul>
  </div>
);

// Mobile Navigation Component
const MobileNav = ({ menuItems, iconColor, walletProps, isOpen, onClose }) => (
  <div id="sidenav" className="nav__menu-box">
    <button 
      className="closebtn" 
      onClick={onClose}
      aria-label="Close mobile menu"
    >
      &times;
    </button>
    <ul className="nav__menu-items">
      {menuItems.map((item) => (
        <MenuItem 
          key={`mobile-${item.id}`} 
          item={item} 
          iconColor={iconColor}
          onClick={onClose}
          isMobile
        />
      ))}
      <WalletButton {...walletProps} isMobile />
    </ul>
  </div>
);

// Main Header Component
const Header = memo(() => {
  const { themeConfig, walletAddress, connectWallet, constants } = useAppContext();
  const location = useLocation();
  
  // Custom hooks
  const isScrolled = useScrollPosition(SCROLL_THRESHOLD);
  const displayAddress = useWalletDisplay(walletAddress, constants?.NETWORK_URL);
  const { openMenu, closeMenu } = useMobileMenu();
  
  // Memoized values
  const currentPath = useMemo(() => {
    const path = location.pathname.substring(1); // Remove leading slash
    return ACTIVE_ROUTES.includes(path) ? path : '';
  }, [location.pathname]);
  
  const iconColor = useMemo(() => {
    const logoKey = themeConfig?.APP_MAIN_LOGO || 'yellow';
    return COLOR_MAP[logoKey] || COLOR_MAP.yellow;
  }, [themeConfig?.APP_MAIN_LOGO]);
  
  const headerClassName = useMemo(() => {
    const baseClass = currentPath ? 'active' : '';
    return isScrolled ? `${baseClass} scrolled`.trim() : baseClass;
  }, [currentPath, isScrolled]);
  
  // Wallet connection handler
  const handleWalletClick = useCallback(async () => {
    if (!walletAddress) {
      if (isAndroid || isIOS) {
        const hasWallet = window.ethereum || (window.avalanche && window.avalanche.isAvalanche);
        if (!hasWallet) {
          const fallbackToStore = () => {
            // Prefer Core Wallet for mobile on Avalanche, otherwise MetaMask
            const walletUrl = 'https://metamask.app.link/';
            window.location.replace(walletUrl);
          };
          window.location.replace(`dapp://${window.location.origin.substring(8)}/`);
          setTimeout(fallbackToStore, 700);
          return;
        }
      }
      
      try {
        await connectWallet();
        logInfo('Wallet connection successful');
      } catch (error) {
        logInfo('Wallet connection failed:', error);
      }
    }
  }, [walletAddress, connectWallet]);
  
  // Wallet props for components
  const walletProps = useMemo(() => ({
    walletAddress,
    displayAddress,
    onConnect: handleWalletClick,
  }), [walletAddress, displayAddress, handleWalletClick]);

  return (
    <header className={headerClassName}>
      <nav className="navbar" role="navigation" aria-label="Main navigation">
        {/* Mobile header */}
        <div className="nav__mobile-box">
          <Logo themeConfig={themeConfig} />
          <button
            onClick={openMenu}
            className="ham_menu"
            aria-label="Open mobile menu"
            aria-expanded="false"
          >
            <HamburgerIcon color="white" />
          </button>
        </div>
        
        {/* Desktop navigation */}
        <DesktopNav
          menuItems={MENU_ITEMS.filter(item => !item.hidden)}
          iconColor={iconColor}
          walletProps={walletProps}
        />

        {/* Mobile navigation */}
        <MobileNav
          menuItems={MENU_ITEMS.filter(item => !item.hidden)}
          iconColor={iconColor}
          walletProps={walletProps}
          onClose={closeMenu}
        />
      </nav>
    </header>
  );
});

Header.displayName = 'Header';

export default Header;
